import { apiFetch, apiFetchEnvelope, type ListMeta } from "@core/http";
import type { components } from "@core/types/openapi";

export type Member = components["schemas"]["Member"];
export type MemberSummary = components["schemas"]["MemberSummary"];
export type MemberInput = components["schemas"]["MemberInput"];
// Semántica de la edición (PUT, decisión 2026-09-29): un `phone`/
// `purchaseDate` AUSENTE significa "no tocar" y un `null` explícito,
// "borrar". El contrato ya declara ambos `nullable`; el alias nombra el
// cuerpo del PUT frente al del alta (`toCreateInput`), que nunca borra.
export type MemberUpdateInput = MemberInput;
export type CatalogEntry = components["schemas"]["CatalogEntry"];
export type Advisor = components["schemas"]["AdvisorEntry"];
export type InvitationStatus = MemberSummary["invitationStatus"] | "all";
export type ActiveFilter = "all" | "active" | "inactive";

export interface MembersQuery {
  search: string;
  invitationStatus: InvitationStatus;
  active: ActiveFilter;
  cursor?: string;
}
export interface MembersPage {
  items: MemberSummary[];
  meta: ListMeta;
}
const base = "/v1/portal/members";
// QA-MTZ-002 #3: la tabla muestra 8 filas por página (PER=8, admin.jsx:701).
// Sin `limit` la API devolvía su valor por defecto (50) y la tarjeta de alto
// fijo recortaba todo lo que pasaba de la octava fila. La paginación sigue
// siendo por cursor (`meta.next`).
export const MEMBERS_PAGE_SIZE = 8;

export function memberSearchParams(query: MembersQuery) {
  const params = new URLSearchParams();
  if (query.search.trim()) params.set("search", query.search.trim());
  if (query.invitationStatus !== "all")
    params.set("invitationStatus", query.invitationStatus);
  if (query.active !== "all")
    params.set("active", String(query.active === "active"));
  if (query.cursor) params.set("cursor", query.cursor);
  params.set("limit", String(MEMBERS_PAGE_SIZE));
  return params;
}
export async function listMembers(query: MembersQuery): Promise<MembersPage> {
  const params = memberSearchParams(query);
  const envelope = await apiFetchEnvelope<MemberSummary[]>(`${base}?${params}`);
  return {
    items: envelope.data,
    meta: envelope.meta ?? { next: null, total: envelope.data.length },
  };
}
export const getMember = (id: string) =>
  apiFetch<Member>(`${base}/${encodeURIComponent(id)}`);
export const createMember = (input: MemberInput) =>
  apiFetch<Member>(base, { method: "POST", body: input });
// El alta nunca "borra" nada: un campo vacío simplemente no viaja.
export function toCreateInput({
  phone,
  purchaseDate,
  ...rest
}: MemberUpdateInput): MemberInput {
  return {
    ...rest,
    ...(phone ? { phone } : {}),
    ...(purchaseDate ? { purchaseDate } : {}),
  };
}
export const editMember = (id: string, input: MemberUpdateInput) =>
  apiFetch<Member>(`${base}/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: input,
  });
export const inviteMember = (id: string) =>
  apiFetch<{ maskedEmail?: string; validityDays?: number }>(
    `${base}/${encodeURIComponent(id)}/invitations`,
    { method: "POST" },
  );
export const inviteMembersBatch = (memberIds: string[]) =>
  apiFetch<{ sent: number; skipped: number }>(`${base}/invitations`, {
    method: "POST",
    body: { memberIds },
  });
// QA-MTZ-002 #10: `Idempotency-Key` es `required: true` en este endpoint
// (api/contracts/parts/00-base.yaml:554-561) — sin ella
// `express-openapi-validator` rechaza con 400 antes de llegar al controlador,
// y el `409 CONFIRM_INACTIVATE` que abre el diálogo de confirmación nunca
// ocurre. Mismo criterio que `reverseRedemption` (redemptions/api.ts): la
// clave la fija la pantalla (una por intento, reusada en el reintento
// confirmado), no este cliente.
export const inactivateMember = (
  id: string,
  input: { inactivationReasonId: string; detail?: string; confirmed: boolean },
  idempotencyKey: string,
) =>
  apiFetch<Member>(`${base}/${encodeURIComponent(id)}/inactivate`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
export const reactivateMember = (
  id: string,
  input: { purchaseDate: string; reason: string },
) =>
  apiFetch<Member>(`${base}/${encodeURIComponent(id)}/reactivate`, {
    method: "POST",
    body: input,
  });
export const listMemberCatalog = (
  catalog: "id-types" | "cities" | "workshops" | "inactivation-reasons",
) => apiFetch<CatalogEntry[]>(`/v1/portal/catalogs/${catalog}/entries`);
// Endpoint propio bajo el módulo `members` (SPEC-202, bug QA-MTZ-002 #4): la
// ruta vieja (`/v1/portal/security/users?profile=advisor`) no existía en el
// contrato y además exigía el permiso `security`, que un Operador —quien sí
// gestiona miembros— no tiene. Solo devuelve `id`/`name`/`workshop`, nunca el
// padrón de usuarios del sistema.
export const listAdvisors = () =>
  apiFetch<Advisor[]>("/v1/portal/members/advisors");

// `Member` (modelo de lectura) trae `city`/`preferredWorkshop` como la
// etiqueta ya resuelta, nunca el id que exige `MemberInput` para editar
// (contrato, sin cambios posibles acá) — mismo problema y misma solución que
// `Vehicle.color`/`Vehicle.model` en `vehicles/api.ts`: se reexporta esa
// implementación en vez de duplicarla (jscpd ya marcaba clon exacto con la
// copia anterior). Si hay más de una entrada con la misma etiqueta, la
// coincidencia es ambigua a propósito: se deja el campo sin resolver (fuerza
// a re-seleccionar) antes que adivinar una de varias.
export { catalogLabel, resolveCatalogId } from "../vehicles/api";

// Advertencia al adelantar la fecha de compra (SPEC-008 §3.5). La usa
// MembersScreen; el modal "Ajustar inicio" del Gran Premio que la compartía
// se retiró (PG-101, PLAN-010 P3).
export const ADVANCED_PURCHASE_DATE_WARNING =
  "Adelantar la fecha de compra puede incorporar compras antiguas al conteo del miembro. Confirma el cambio solo si el dato es correcto.";

// Mapeo único de códigos de error de `members` — un solo lugar para que
// cualquier pantalla que escriba contra este módulo traduzca el mismo código
// al mismo texto, en vez de reusar el mapeo de otro módulo (`prizes`) cuyos
// códigos no son los que este endpoint puede devolver.
// PLAN-009 §3: el mapeo ahora viaja como `codeMessages` a `reportError`
// (toast, tono error) en vez de resolver un texto para pintarlo en pantalla
// — un error de servicio o de operación va siempre al toast, nunca a un
// `<p role="alert">` ni a `setError` de un campo que no es el que falló.
export const MEMBER_ERROR_MESSAGES: Record<string, string> = {
  IDENTIFICATION_DUPLICATE: "Ya existe un miembro con esa identificación",
  EMAIL_DUPLICATE: "Ese correo ya pertenece a otro miembro",
  MEMBER_ALREADY_REGISTERED:
    "Este miembro ya activó su cuenta; no requiere invitación",
  REASON_REQUIRED: "Indica el motivo del cambio",
  NO_ACTIVE_VEHICLE:
    "No se puede reactivar un miembro sin vehículo Mercedes-Benz activo",
};
